// Click tracking for direct-contact CTAs (phone, WhatsApp, email, LinkedIn,
// calendar) and form submissions.
//
// Every click fires two signals:
//   1. the existing analytics layer (GA4 / GTM dataLayer / Clarity)
//   2. a first-party beacon to /api/public/cta-click, stored in the
//      `cta_clicks` table so conversions are visible without depending on a
//      third-party analytics account.

import { trackEvent, type AnalyticsEvent } from "@/lib/analytics";
import { currentVariant } from "@/lib/abTest";

export type CtaChannel =
  | "form_start"
  | "phone"
  | "whatsapp"
  | "email"
  | "linkedin"
  | "calendar"
  | "form";

const EVENT_MAP: Record<CtaChannel, AnalyticsEvent> = {
  phone: "call_click",
  whatsapp: "whatsapp_click",
  email: "email_click",
  linkedin: "linkedin_click",
  calendar: "calendar_click",
  form: "consultation_submit",
  form_start: "form_start",
};

// Visitor session id — tab-scoped, random, non-identifying. Used only to tie a
// CTA click to a later form submission for funnel analysis.
const SESSION_KEY = "akg:sid:v1";

export function getVisitorSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

/** Placement of the most recent CTA interaction in this session. */
const LAST_PLACEMENT_KEY = "akg:last-cta:v1";

export function getLastPlacement(): string {
  if (typeof window === "undefined") return "";
  try {
    return window.sessionStorage.getItem(LAST_PLACEMENT_KEY) ?? "";
  } catch {
    return "";
  }
}

/**
 * Record a CTA click. Safe to call from any onClick handler — never throws,
 * never blocks navigation (uses sendBeacon where available).
 */
export function trackCta(channel: CtaChannel, placement: string) {
  if (typeof window === "undefined") return;

  try {
    trackEvent(EVENT_MAP[channel], { channel, placement });
  } catch {
    /* analytics must never break a click */
  }

  try {
    if (channel !== "form") {
      window.sessionStorage.setItem(LAST_PLACEMENT_KEY, placement);
    }
  } catch {
    /* storage may be blocked */
  }

  const payload = JSON.stringify({
    channel,
    placement,
    sessionId: getVisitorSessionId() || undefined,
    variant: currentVariant() || undefined,
    path: window.location.pathname,
    referrer: document.referrer || undefined,
  });

  try {
    if (typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(
        "/api/public/cta-click",
        new Blob([payload], { type: "application/json" }),
      );
      return;
    }
    void fetch("/api/public/cta-click", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => undefined);
  } catch {
    /* noop */
  }
}

/** Convenience: onClick handler factory. */
export const ctaClick = (channel: CtaChannel, placement: string) => () =>
  trackCta(channel, placement);
